import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import { instituteService } from '../../../services/instituteService';
import { storageService } from '../../../services/storageService';
import { validateImageFile, getImageUploadNotice } from '../../../utils/imageValidation';

const BoothImageManager = () => {
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingImages, setUploadingImages] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedInstitute, setSelectedInstitute] = useState(null);

  useEffect(() => {
    loadInstitutes();
  }, []);

  const loadInstitutes = async () => {
    try {
      setLoading(true);
      const data = await instituteService?.getAllInstitutesForAdmin({ approvalStatus: 'approved' });
      setInstitutes(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load institutes');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (instituteId, imageType, file) => {
    if (!file) return;

    // Validate file using utility
    const validation = validateImageFile(file, imageType);
    if (!validation?.valid) {
      setError(validation?.error);
      return;
    }

    try {
      setError('');
      setSuccess('');
      setUploadingImages(prev => ({ ...prev, [`${instituteId}-${imageType}`]: true }));

      // Upload image to storage
      const { publicUrl } = await storageService?.uploadBoothImage(file, instituteId, imageType);

      // Update institute profile with new image URL
      const imageUrls = imageType === 'logo' 
        ? { logoUrl: publicUrl }
        : { bannerUrl: publicUrl };
      
      await storageService?.updateInstituteImages(instituteId, imageUrls);

      // Refresh institutes list
      await loadInstitutes();

      setSuccess(`${imageType === 'logo' ? 'Logo' : 'Banner'} uploaded successfully!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to upload image');
    } finally {
      setUploadingImages(prev => {
        const newState = { ...prev };
        delete newState?.[`${instituteId}-${imageType}`];
        return newState;
      });
    }
  };

  const triggerFileInput = (instituteId, imageType) => {
    const input = document?.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp,image/jpg';
    input.onchange = (e) => {
      const file = e?.target?.files?.[0];
      if (file) {
        handleImageUpload(instituteId, imageType, file);
      }
    };
    input?.click();
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-3 text-muted-foreground">Loading institutes...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="border-b border-border p-4 md:p-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Image" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
              Booth Image Management
            </h2>
            <p className="text-sm text-muted-foreground">Upload and manage booth logos and banners for all institutes</p>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6">
        {/* Image Size Guidelines */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">Image Size Requirements</h3>
              <div className="text-sm text-blue-800 space-y-2">
                <div>
                  <strong>Logo:</strong> Recommended 250×100px (2.5:1 ratio) | Max 100KB | PNG/JPG/WebP
                </div>
                <div>
                  <strong>Banner:</strong> Recommended 1920×600px (16:5 ratio) | Max 500KB | JPG/PNG/WebP
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2">
            <Icon name="AlertCircle" size={20} className="flex-shrink-0 mt-0.5" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-start gap-2">
            <Icon name="CheckCircle" size={20} className="flex-shrink-0 mt-0.5" />
            <span className="text-sm">{success}</span>
          </div>
        )}

        {institutes?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Building2" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
            <p className="text-muted-foreground">No approved institutes found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {institutes?.map((institute) => (
              <div key={institute?.id} className="bg-muted rounded-lg p-4 hover:bg-muted/80 transition-smooth">
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Institute Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                      {institute?.instituteName}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                      <Icon name="Store" size={16} />
                      <span>Booth Type: {institute?.boothType}</span>
                    </div>

                    {/* Logo Section */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Booth Logo
                        <span className="block text-xs text-muted-foreground mt-1">
                          {getImageUploadNotice('logo')}
                        </span>
                      </label>
                      <div className="flex items-center gap-4">
                        <div className="w-24 h-24 bg-background rounded-lg overflow-hidden border border-border flex items-center justify-center">
                          {institute?.logoUrl ? (
                            <Image
                              src={institute?.logoUrl}
                              alt={`${institute?.instituteName} logo`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Icon name="Building2" size={32} color="var(--color-muted-foreground)" />
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Upload"
                          onClick={() => triggerFileInput(institute?.id, 'logo')}
                          loading={uploadingImages?.[`${institute?.id}-logo`]}
                          disabled={uploadingImages?.[`${institute?.id}-logo`]}
                        >
                          {institute?.logoUrl ? 'Change Logo' : 'Upload Logo'}
                        </Button>
                      </div>
                    </div>

                    {/* Banner Section */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Booth Banner
                        <span className="block text-xs text-muted-foreground mt-1">
                          {getImageUploadNotice('banner')}
                        </span>
                      </label>
                      <div className="flex flex-col gap-4">
                        <div className="w-full h-32 bg-background rounded-lg overflow-hidden border border-border flex items-center justify-center">
                          {institute?.bannerUrl ? (
                            <Image
                              src={institute?.bannerUrl}
                              alt={`${institute?.instituteName} banner`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Icon name="Image" size={32} color="var(--color-muted-foreground)" />
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Upload"
                          onClick={() => triggerFileInput(institute?.id, 'banner')}
                          loading={uploadingImages?.[`${institute?.id}-banner`]}
                          disabled={uploadingImages?.[`${institute?.id}-banner`]}
                        >
                          {institute?.bannerUrl ? 'Change Banner' : 'Upload Banner'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BoothImageManager;